import { FC, memo, useMemo } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import { Capitalized } from 'components/atoms/typography'
import { Routes } from '@/core/routes'
import PurchasePromoInfoBox from '@/components/molecules/purchasePromoInfoBox'
import ProductDetailPlainInfo from './productDetailPlainInfo'
import useTranslation from 'next-translate/useTranslation'
import Link from '@/components/atoms/link'
import {
  MarketCode,
  MultiDiscount,
  Product,
  ProductSize
} from '@/network/graphql.g'
import { ProductSideMenuActionPart } from '@/components/organisms/productDetailSideMenu/productSideMenuActionPart'
import { makePriceString } from '@/core/toStringHelper'
import { NextArrowIconDefault } from '@/components/atoms/icons/NextArrowIconDefault'
import { ProductDetailBanners } from './productDetailBanners'
import styled from 'styled-components'
import { transparentize } from 'polished'
import { useAsset } from '@/providers/assetsProvider'
import { useUser } from '@/providers/userProvider'
import { useDomain } from '@/providers/storeProvider'

type ProductDetailSideMenuProps = {
  product: Product
  initialSize?: ProductSize
  modalMode?: boolean
  setHoverColorVariant: (hoverColorVariant: string) => void
  onDidTapClose?: () => void
  multiDiscount: MultiDiscount
  scrollToProductDetailRating?: () => void
  hasSizeInUrl?: boolean
  serverSelectedSize?: ProductSize | null
} & Rest

const ProductDetailSideMenu: FC<ProductDetailSideMenuProps> = memo(
  ({
    product,
    initialSize,
    modalMode = false,
    setHoverColorVariant,
    onDidTapClose,
    multiDiscount,
    scrollToProductDetailRating,
    hasSizeInUrl,
    serverSelectedSize,
    ...rest
  }) => {
    const { subdomainName } = useDomain()
    const isFreeDeliveryHided = [
      MarketCode.Bg,
      MarketCode.Hr,
      MarketCode.Gr
    ].includes(subdomainName as MarketCode)

    const { t } = useTranslation('common')
    const { isEmployee } = useUser()
    const {
      id,
      canRevertProduct,
      isFreeShipping,
      freeShippingFrom,
      currency,
      prices
    } = useMemo(() => product, [product]) ?? {}

    const productBanners = useMemo(() => product?.productBanners, [product.id])

    const getPriceForDelivery = makePriceString({
      price: freeShippingFrom,
      currency: currency
    }).replace(currency.rightSymbol, '')

    const makePriceForDelivery = getPriceForDelivery.endsWith('.00')
      ? getPriceForDelivery.slice(0, -3).concat(currency.rightSymbol)
      : getPriceForDelivery.concat(currency.rightSymbol)

    const productDetailPlainInfoPrice = makePriceString({
      price: prices?.minLastMonthPrice,
      currency: currency
    })

    const { useSOIPricing } = useAsset()

    return (
      <>
        <Box {...rest} data-testid="productDetailSideMenu">
          <ProductSideMenuActionPart
            initialSize={initialSize}
            product={product}
            modalMode={modalMode}
            setHoverColorVariant={setHoverColorVariant}
            onDidTapClose={onDidTapClose}
            scrollToProductDetailRating={scrollToProductDetailRating}
            multiDiscount={multiDiscount}
            hasSizeInUrl={hasSizeInUrl}
            serverSelectedSize={serverSelectedSize}
          />
          {!modalMode ? (
            <>
              <Box mt={23} data-testid="productDetailPlainInfo">
                <Box my={10}>
                  <ProductDetailBanners productBanners={productBanners} />
                </Box>
              </Box>
              {!useSOIPricing ? (
                <Box mb={20}>
                  <ProductDetailPlainInfo
                    title={`${t('ProductDetail.SellerInfoPrice.text', {
                      price: productDetailPlainInfoPrice
                    })}`}
                  />
                </Box>
              ) : null}
              {!(
                isFreeDeliveryHided &&
                !isFreeShipping &&
                !canRevertProduct
              ) && (
                <PurchasePromoInfoBoxWrapper>
                  <PurchasePromoInfoBox
                    info={{ moreInfoUrl: Routes.login }}
                    canRevertProduct={canRevertProduct}
                  />
                  {!isFreeDeliveryHided && !isFreeShipping && freeShippingFrom && (
                    <PurchasePromoInfoBox
                      info={{
                        freeShippingInfo: t(
                          'ProductDetail.Promo.freeShipping',
                          {
                            price: makePriceForDelivery
                          }
                        )
                      }}
                      canRevertProduct={canRevertProduct}
                    />
                  )}
                  {!modalMode && isFreeShipping && !isEmployee && (
                    <PurchasePromoInfoBox
                      info={{
                        freeShippingInfo: t(
                          'ProductDetail.Promo.freeShippingPromoCode'
                        )
                      }}
                    />
                  )}
                </PurchasePromoInfoBoxWrapper>
              )}
            </>
          ) : (
            <Link href={`${Routes.product}/${id}`}>
              <Flex data-testid="productDetailButton">
                <Capitalized>
                  {t('ProductDetail.goToDetail').toUpperCase()}
                </Capitalized>
                <NextArrowIconDefault width={12} height={8} />
              </Flex>
            </Link>
          )}
        </Box>
      </>
    )
  }
)

export default ProductDetailSideMenu

const PurchasePromoInfoBoxWrapper = styled(Box)`
  border: 1px solid
    ${(props) => transparentize(0.8, props.theme.colors.onBackground)};
  border-radius: 2px;
  padding: 4px 0;
  background-color: ${(props) => props.theme.colors.greyBackground};
`
